import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../services/firebase';

const SETTINGS_COLLECTION = 'systemSettings';
const SCHEDULING_SETTINGS_DOC = 'schedulingSettings';
const CACHE_DURATION = 5 * 60 * 1000;
const syncSchedulingCalendarFn = httpsCallable(functions, 'syncSchedulingCalendar', {
  timeout: 540000
});

let settingsCache = null;
let cacheTimestamp = 0;

const normalizeEmailList = (emails = []) => Array.from(new Set((emails || [])
  .map((email) => `${email || ''}`.trim().toLowerCase())
  .filter(Boolean)));

const normalizeNamedItem = (item = {}, prefix = 'item') => ({
  id: item.id || `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: item.name || '',
  active: item.active !== false
});

const normalizeRole = (role = {}) => ({
  ...normalizeNamedItem(role, 'role'),
  color: /^#[0-9a-fA-F]{6}$/.test(role.color || '') ? role.color : '#1976d2'
});

const normalizeTimeSlot = (slot = {}) => ({
  id: slot.id || `time_slot_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: slot.name || '',
  startTime: slot.startTime || '06:00',
  endTime: slot.endTime || '18:00',
  active: slot.active !== false
});

export const getDefaultSchedulingSettings = () => ({
  schedulingAdminEmails: [],
  shiftRoles: [
    { id: 'crew', name: 'Crew', color: '#1976d2', active: true },
    { id: 'driver', name: 'Driver', color: '#2e7d32', active: true },
    { id: 'officer', name: 'Officer', color: '#ed6c02', active: true }
  ],
  locations: [
    { id: 'station', name: 'Station', active: true }
  ],
  timeSlots: [
    { id: 'am_0600_1800', name: 'AM', startTime: '06:00', endTime: '18:00', active: true },
    { id: 'pm_1800_0600', name: 'PM', startTime: '18:00', endTime: '06:00', active: true }
  ],
  openShiftClaimMode: 'request',
  weekStartsOn: 0,
  googleCalendarSync: {
    enabled: false,
    calendarId: '',
    syncUnpublished: false,
    lastFullSyncAt: null,
    lastFullSyncSummary: null,
    lastSyncError: ''
  }
});

export const normalizeSchedulingSettings = (settings = {}) => ({
  ...getDefaultSchedulingSettings(),
  ...settings,
  schedulingAdminEmails: normalizeEmailList(settings.schedulingAdminEmails),
  shiftRoles: (settings.shiftRoles || getDefaultSchedulingSettings().shiftRoles).map(normalizeRole),
  locations: (settings.locations || getDefaultSchedulingSettings().locations).map((location) => normalizeNamedItem(location, 'location')),
  timeSlots: (settings.timeSlots || getDefaultSchedulingSettings().timeSlots).map(normalizeTimeSlot),
  openShiftClaimMode: ['claim', 'request'].includes(settings.openShiftClaimMode) ? settings.openShiftClaimMode : 'request',
  weekStartsOn: Number.isInteger(Number(settings.weekStartsOn)) && Number(settings.weekStartsOn) >= 0 && Number(settings.weekStartsOn) <= 6
    ? Number(settings.weekStartsOn)
    : 0,
  googleCalendarSync: {
    ...getDefaultSchedulingSettings().googleCalendarSync,
    ...(settings.googleCalendarSync || {}),
    enabled: settings.googleCalendarSync?.enabled === true,
    calendarId: `${settings.googleCalendarSync?.calendarId || ''}`.trim(),
    syncUnpublished: settings.googleCalendarSync?.syncUnpublished === true
  }
});

export const getSchedulingSettings = async (useCache = true) => {
  try {
    const now = Date.now();
    if (useCache && settingsCache && (now - cacheTimestamp) < CACHE_DURATION) {
      return settingsCache;
    }

    const settingsRef = doc(db, SETTINGS_COLLECTION, SCHEDULING_SETTINGS_DOC);
    const settingsDoc = await getDoc(settingsRef);
    const settings = settingsDoc.exists()
      ? normalizeSchedulingSettings(settingsDoc.data())
      : getDefaultSchedulingSettings();

    settingsCache = settings;
    cacheTimestamp = now;
    return settings;
  } catch (error) {
    console.error('Error getting scheduling settings:', error);
    return getDefaultSchedulingSettings();
  }
};

export const saveSchedulingSettings = async (settings, updatedBy) => {
  try {
    const normalizedSettings = normalizeSchedulingSettings(settings);
    await setDoc(doc(db, SETTINGS_COLLECTION, SCHEDULING_SETTINGS_DOC), {
      ...normalizedSettings,
      lastUpdated: serverTimestamp(),
      updatedBy: updatedBy || ''
    });

    settingsCache = null;
    cacheTimestamp = 0;
    return normalizedSettings;
  } catch (error) {
    console.error('Error saving scheduling settings:', error);
    throw new Error('Failed to save scheduling settings. Please try again.');
  }
};

export const isSchedulingAdmin = (settings = {}, userEmail = '') => (
  normalizeEmailList(settings.schedulingAdminEmails).includes(`${userEmail || ''}`.trim().toLowerCase())
);

export const setSchedulingAdminEmailAssigned = async (email, assigned, updatedBy) => {
  const normalizedEmail = normalizeEmailList([email])[0];
  if (!normalizedEmail) {
    throw new Error('A valid email is required.');
  }

  const currentSettings = await getSchedulingSettings(false);
  const currentEmails = normalizeEmailList(currentSettings.schedulingAdminEmails);
  const nextEmails = assigned
    ? Array.from(new Set([...currentEmails, normalizedEmail]))
    : currentEmails.filter((currentEmail) => currentEmail !== normalizedEmail);

  return saveSchedulingSettings({
    ...currentSettings,
    schedulingAdminEmails: nextEmails
  }, updatedBy);
};

export const runSchedulingCalendarFullSync = async () => {
  try {
    const result = await syncSchedulingCalendarFn({ mode: 'full' });
    return result.data;
  } catch (error) {
    console.error('Error running scheduling calendar sync:', error);
    throw new Error(error.message || 'Failed to sync Scheduling calendar.');
  }
};

export const buildSchedulingRole = () => normalizeRole({ name: '', color: '#1976d2' });
export const buildSchedulingLocation = () => normalizeNamedItem({ name: '' }, 'location');
export const buildSchedulingTimeSlot = () => normalizeTimeSlot({ name: '' });
